import webApiClient from "./client";

/**
 * Billing API Client — bank transfer (free feature post-pivot 2026-05-10)
 *
 * Wraps /api/v2/billing/* endpoints for bank details, offering toggle, and
 * reconciliation search. Each method returns the parsed response data on
 * success, throws on failure.
 *
 * Also covers the two mark-bank-transfer endpoints from the per-resource
 * routes (/classes/payments/* and /booking/:id/*) since they're conceptually
 * part of the bank-transfer flow.
 */

export const billingApi = {
    // ─── Offering / bank-details state ───────────────────────────────────────

    /**
     * GET /api/v2/billing/addon-state
     * @returns {Promise<{ offeringEnabled: boolean, bankDetails: { bsb: string, accountName: string, accountNumber: string } }>}
     */
    getAddonState: async () => {
        const response = await webApiClient.get("/api/v2/billing/addon-state");
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to load bank transfer state");
        }
        return response.data;
    },

    // ─── Bank details ────────────────────────────────────────────────────────

    /**
     * PUT /api/v2/billing/bank-details
     * @param {string} bsb - format XXX-XXX
     * @param {string} accountName - 1-100 chars
     * @param {string} accountNumber - 6-10 digits
     */
    saveBankDetails: async (bsb, accountName, accountNumber) => {
        const response = await webApiClient.put("/api/v2/billing/bank-details", {
            bsb,
            accountName,
            accountNumber,
        });
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to save bank details");
        }
        return response.data;
    },

    // ─── Offering toggle ─────────────────────────────────────────────────────

    /**
     * PUT /api/v2/billing/offering-enabled
     * @param {boolean} enabled
     */
    setOfferingEnabled: async (enabled) => {
        const response = await webApiClient.put("/api/v2/billing/offering-enabled", { enabled });
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to update offering");
        }
        return response.data;
    },

    // ─── Reconciliation ──────────────────────────────────────────────────────

    /**
     * GET /api/v2/billing/reconciliation/search — reconcile worklist over the transactions ledger.
     * Lists ALL external payments AND external refunds (reconciled + unreconciled) for the org.
     * @param {object} filters - { q, type, rail, reconciled, direction, dateFrom, dateTo }
     *   rail: 'all'|'bank_transfer'|'eftpos'|'cash'|'other'; reconciled: 'all'|'true'|'false';
     *   direction: 'all'|'in'|'out' (in = payment, out = refund)
     * @returns {Promise<{ results: Array, total: number, summary: Array<{currency,in,out,net,count}> }>}
     *   Each row carries `direction` ('in'|'out') and a positive `amount` magnitude; `summary` is the
     *   server-computed per-currency net (in cents) across both directions.
     */
    searchReconciliation: async (filters = {}) => {
        const { q = "", type = "all", rail = "all", reconciled = "all", direction = "all", dateFrom, dateTo } = filters;
        const params = new URLSearchParams({ q, type, rail, reconciled, direction });
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo);
        const response = await webApiClient.get(`/api/v2/billing/reconciliation/search?${params.toString()}`);
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Reconciliation search failed");
        }
        return response.data;
    },

    /**
     * PUT /api/v2/billing/reconciliation/:transactionId/reconcile — mark an external payment
     * reconciled (accounting tick). Distinct from mark-paid; the row is already paid/green.
     * @param {string} transactionId
     * @param {object} body - { reconciledAgainst?: string|null, rail?: 'bank_transfer'|'eftpos'|'cash'|'other' }
     */
    reconcileTransaction: async (transactionId, body = {}) => {
        const response = await webApiClient.put(`/api/v2/billing/reconciliation/${transactionId}/reconcile`, body);
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to reconcile payment");
        }
        return response.data;
    },

    // Note: marking a payment paid externally now happens in AdminPaymentModal
    // (classApi.markExternalPayment) / booking admin — the Reconcile Payments page is
    // reconcile-only. The old markEnrolmentBankTransferPaid / markBookingBankTransferPaid
    // wrappers were removed with that page rebuild (no remaining consumer).
};
